import {
  ScreeningSurveyAttributesSchema,
  type ScreeningSurveyStatusResponse,
} from "@clipboard-health/contract-home-health-api";
import { type z } from "zod";

export type ExperienceLevel = z.infer<
  typeof ScreeningSurveyAttributesSchema
>["homeHealthExperience"];
export type HospiceExperienceLevel = z.infer<
  typeof ScreeningSurveyAttributesSchema
>["hospiceExperience"];
export type OasisAssessmentRange = z.infer<
  typeof ScreeningSurveyAttributesSchema
>["oasisAssessments"];
export type ScreeningSurveyStatus = ScreeningSurveyStatusResponse["data"]["attributes"]["status"];

export const experienceLevelValues =
  ScreeningSurveyAttributesSchema.shape.homeHealthExperience.options;
export const hospiceExperienceLevelValues =
  ScreeningSurveyAttributesSchema.shape.hospiceExperience.options;
export const oasisAssessmentRangeValues =
  ScreeningSurveyAttributesSchema.shape.oasisAssessments.options;

export type EmrSystem =
  | "KINNSER"
  | "WELLSKY"
  | "HOMECARE_HOMEBASE"
  | "AXXESS"
  | "MATRIXCARE"
  | "ALORA_HEALTH"
  | "SYNERGY_EMR"
  | "CAREFICIENT"
  | "HOSPICE_TOOLS"
  | "DATA_SOFT_LOGIC"
  | "CAREVOYANT"
  | "KANTIME"
  | "AXISCARE"
  | "ALAYACARE"
  | "NONE"
  | "OTHER";

export type Language =
  | "SPANISH"
  | "MANDARIN"
  | "KOREAN"
  | "JAPANESE"
  | "ARMENIAN"
  | "HINDI"
  | "ARABIC"
  | "TAGALOG"
  | "VIETNAMESE"
  | "FARSI"
  | "RUSSIAN"
  | "HMONG"
  | "NONE";

export type SpecialtyCare =
  | "WOUND_CARE"
  | "WOUND_VAC"
  | "PICC_LINE"
  | "DEMENTIA"
  | "TRACH"
  | "IV_INFUSIONS"
  | "G_TUBE"
  | "NONE";

export const experienceLevelLabels: Record<ExperienceLevel, string> = {
  NO_EXPERIENCE: "No experience",
  LESS_THAN_1_YEAR: "Less than 1 year",
  ONE_TO_THREE_YEARS: "1-3 years",
  FOUR_TO_SEVEN_YEARS: "4-7 years",
  EIGHT_TO_ELEVEN_YEARS: "8-11 years",
  TWELVE_PLUS_YEARS: "12+ years",
};

export const hospiceExperienceLevelLabels: Record<HospiceExperienceLevel, string> = {
  NO_EXPERIENCE: "No experience",
  LESS_THAN_6_MONTHS: "Less than 6 months",
  SIX_MONTHS_TO_1_YEAR: "6 months - 1 year",
  ONE_TO_THREE_YEARS: "1-3 years",
  FOUR_TO_SEVEN_YEARS: "4-7 years",
  EIGHT_TO_ELEVEN_YEARS: "8-11 years",
  TWELVE_PLUS_YEARS: "12+ years",
};

export const oasisAssessmentRangeLabels: Record<OasisAssessmentRange, string> = {
  ZERO: "0",
  ONE_TO_TEN: "1-10",
  ELEVEN_TO_FIFTY: "11-50",
  FIFTY_PLUS: "50+",
};

export const emrSystemLabels: Record<EmrSystem, string> = {
  KINNSER: "Kinnser",
  WELLSKY: "WellSky",
  HOMECARE_HOMEBASE: "HomeCare HomeBase",
  AXXESS: "Axxess",
  MATRIXCARE: "MatrixCare",
  ALORA_HEALTH: "Alora Health",
  SYNERGY_EMR: "Synergy EMR",
  CAREFICIENT: "Careficient",
  HOSPICE_TOOLS: "Hospice Tools",
  DATA_SOFT_LOGIC: "Data Soft Logic",
  CAREVOYANT: "CareVoyant",
  KANTIME: "KanTime",
  AXISCARE: "AxisCare",
  ALAYACARE: "AlayaCare",
  NONE: "None",
  OTHER: "Other (Please Specify Below)",
};

export const languageLabels: Record<Language, string> = {
  SPANISH: "Spanish",
  MANDARIN: "Mandarin",
  KOREAN: "Korean",
  JAPANESE: "Japanese",
  ARMENIAN: "Armenian",
  HINDI: "Hindi",
  ARABIC: "Arabic",
  TAGALOG: "Tagalog",
  VIETNAMESE: "Vietnamese",
  FARSI: "Farsi",
  RUSSIAN: "Russian",
  HMONG: "Hmong",
  NONE: "None",
};

export const specialtyCareLabels: Record<SpecialtyCare, string> = {
  WOUND_CARE: "Wound Care",
  WOUND_VAC: "Wound VAC",
  PICC_LINE: "PICC Line",
  DEMENTIA: "Dementia",
  TRACH: "Trach",
  IV_INFUSIONS: "IV Infusions",
  G_TUBE: "G-Tube",
  NONE: "None",
};
